import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {AbstractComponent, ConfirmationModalComponent} from "@components";
import {
  Country,
  EventItem,
  Lookups,
  Race,
  RaceRoute,
  Waypoint, WaypointType
} from "@model";
import {LoaderService, RaceRouteService, TOAST_STATE, ToastService, WaypointService} from "@services";
import {required} from "@utility";
import {environment} from "@environment";
import {isPlatformBrowser} from "@angular/common";

@Component({
  selector: 'app-race-waypoints',
  templateUrl: './race-waypoints.component.html',
  styleUrls: ['./race-waypoints.component.scss']
})
export class RaceWaypointsComponent extends AbstractComponent implements OnInit {
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() disableEditing: boolean = false;
  @Input() countries: Country[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) waypointMarkerTypes: Lookups[] = [];
  @Input() waypointTypes: Lookups[] | undefined;
  @Input() waypointList: Waypoint[] = [];
  @Input() raceRoute: RaceRoute | undefined;
  @Output() waypointListChange: EventEmitter<Waypoint[]> = new EventEmitter<Waypoint[]>();
  @Output() raceRouteChange: EventEmitter<RaceRoute | undefined> = new EventEmitter<RaceRoute | undefined>();

  @ViewChild('fileUpload') fileUploadInputVariable?: ElementRef;

  gpx?: any;

  public isTableCollapsed: boolean = false;
  isPointSelectionEnabled: boolean = false;
  currentWaypoint: Waypoint = new Waypoint();

  inputForm = new FormGroup({
    longitude: new FormControl(),
    latitude: new FormControl(),
    pointNameHr: new FormControl(),
    pointNameEn: new FormControl(),
    pointNameSlo: new FormControl(),
    pointNameIt: new FormControl(),
    pointSelection: new FormControl(),
    distanceFromStart: new FormControl(),
    elevation: new FormControl(),
    ordinalNumber: new FormControl(),
    waypointTypeId: new FormControl(),
    city: new FormControl(),
    address: new FormControl(),
    country: new FormControl(),
    additionalMark: new FormControl(),
    descriptionHr: new FormControl(),
    descriptionEn: new FormControl(),
    descriptionIt: new FormControl(),
    descriptionSlo: new FormControl(),
    markerTypes: new FormControl(),
    limit: new FormControl(),
  });

  constructor(public translocoService: TranslocoService, private modalService: NgbModal,
              private waypointService: WaypointService, private loaderService: LoaderService,
              private toastService: ToastService, private raceRouteService: RaceRouteService,
              @Inject(PLATFORM_ID) private platformId: Object) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.parseGpx();
    this.subscribeToWaypointTypeChange();
    if (this.waypointList && this.waypointList.length > 0) {
      this.isTableCollapsed = false;
    }
  }

  private parseGpx(): void {
    if (this.raceRoute && this.raceRoute.content) {
      if (isPlatformBrowser(this.platformId)) {
        import("../../../../../../components/leaflet-maps/gpx-layer").then(({GPX}) => {
          this.gpx = new GPX(this.raceRoute!.content, {}, () => {
          });
        });
      }
    }
  }

  private initForm() {
    this.inputForm.controls.waypointTypeId.addValidators([required]);
    this.inputForm.controls.pointNameHr.addValidators([required]);
    if (this.disableEditing) {
      this.inputForm.disable();
    }
  }

  onSelectPoint(location: any) {
    this.setSelectedPointData(location);
  }

  private setSelectedPointData(location: any) {
    this.inputForm.controls.latitude.setValue(location.lat);
    this.inputForm.controls.longitude.setValue(location.lng);
    if (location.meta) {
      this.inputForm.controls.elevation.setValue(parseInt(location.meta.ele));
      this.inputForm.controls.distanceFromStart.setValue(parseInt(location.meta.distanceFromStart));
    }
    this.currentWaypoint = {...this.currentWaypoint};
    this.currentWaypoint.latitude = location.lat;
    this.currentWaypoint.longitude = location.lng;
    if (location.meta) {
      this.currentWaypoint.elevation = parseInt(location.meta.ele);
      this.currentWaypoint.distanceFromStart = parseInt(location.meta.distanceFromStart);
    }
  }

  private subscribeToWaypointTypeChange() {
    this.subscriptions.push(this.inputForm.controls.waypointTypeId.valueChanges.subscribe((waypointTypeId: number) => {
      let requiredFields: FormControl[] = [];
      let nonRequiredFields: FormControl[] = [];
      if (waypointTypeId === WaypointType.START_WAYPOINT || waypointTypeId === WaypointType.FINISH_WAYPOINT || waypointTypeId === WaypointType.START_FINISH_WAYPOINT) {
        requiredFields = [this.inputForm.controls.city, this.inputForm.controls.address,
          this.inputForm.controls.additionalMark, this.inputForm.controls.country];
      } else {
        nonRequiredFields = [this.inputForm.controls.city, this.inputForm.controls.address,
          this.inputForm.controls.additionalMark, this.inputForm.controls.country];
      }
      if (waypointTypeId === WaypointType.CONTROL_WAYPOINT) {
        requiredFields = [...requiredFields, this.inputForm.controls.ordinalNumber];
      } else {
        nonRequiredFields = [...nonRequiredFields, this.inputForm.controls.ordinalNumber];
      }
      requiredFields.forEach((control: FormControl) => {
        control.addValidators([required]);
        control.updateValueAndValidity();
      });
      nonRequiredFields.forEach((control: FormControl) => {
        control.removeValidators([required]);
        control.updateValueAndValidity();
      });
    }));
  }

  togglePointSelection() {
    this.isPointSelectionEnabled = !this.isPointSelectionEnabled;
  }

  onSave(): void {
    if (!this.inputForm.valid || this.disableEditing) {
      return;
    } else if (!this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    } else {
      let waypoint: Waypoint = this.getWaypointFields();
      if (!this._isWaypointValid(waypoint)) {
        return;
      }
      waypoint.raceId = this.race?.id;
      waypoint.eventId = this.event?.id;
      this.loaderService.startLoading();
      this.waypointService.updateWaypoint(waypoint, () => {
        this.currentWaypoint = new Waypoint();
        this.isPointSelectionEnabled = false;
        this.inputForm.reset();
        this.waypointService.findWaypoints(this.race?.id).subscribe((list: Waypoint[]) => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("organiser.event.details.control.point.save.success"));
          this.waypointList = list;
          this.waypointListChange.emit(list);
        });
      }, () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("organiser.event.details.control.point.save.error"))
      });
    }
  }

  /**
   * Check that there isn't already a waypoint with same data in list of waypoints
   * @param waypointItem
   */
  _isWaypointValid(waypointItem: Waypoint): boolean {
    let isWaypointValid: boolean = true;
    if (!this.waypointList || this.waypointList.length === 0) {
      return isWaypointValid;
    }
    if (waypointItem.waypointTypeId === WaypointType.FINISH_WAYPOINT) {
      let waypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.FINISH_WAYPOINT);
      if (waypoint && waypoint.id !== waypointItem.id) {
        isWaypointValid = false;
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("organiser.event.details.control.point.error.existingFinishWaypoint"));
      } else {
        let waypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.START_FINISH_WAYPOINT);
        if (waypoint) {
          isWaypointValid = false;
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("organiser.event.details.control.point.error.existingStartFinishWaypoint"));
        }
      }
    } else if (waypointItem.waypointTypeId === WaypointType.START_WAYPOINT) {
      let waypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.START_WAYPOINT);
      if (waypoint && waypoint.id !== waypointItem.id) {
        isWaypointValid = false;
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("organiser.event.details.control.point.error.existingStartWaypoint"));
      } else {
        let waypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.START_FINISH_WAYPOINT);
        if (waypoint) {
          isWaypointValid = false;
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("organiser.event.details.control.point.error.existingStartFinishWaypoint"));
        }
      }
    } else if (waypointItem.waypointTypeId === WaypointType.CONTROL_WAYPOINT) {
      let waypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.CONTROL_WAYPOINT && waypoint.ordinalNumber === waypointItem.ordinalNumber);
      if (waypoint && waypoint.id !== waypointItem.id) {
        isWaypointValid = false;
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("organiser.event.details.control.point.error.existingControlWaypoint"));
      }
    } else if (waypointItem.waypointTypeId === WaypointType.START_FINISH_WAYPOINT) {
      let waypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.START_FINISH_WAYPOINT);
      if (waypoint && waypoint.id !== waypointItem.id) {
        isWaypointValid = false;
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("organiser.event.details.control.point.error.existingStartFinishWaypoint"));
      } else {
        let startWaypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.START_WAYPOINT);
        let finishWaypoint: Waypoint | undefined = this.waypointList?.find((waypoint: Waypoint) => waypoint.waypointTypeId === WaypointType.START_WAYPOINT);

        if (startWaypoint) {
          isWaypointValid = false;
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("organiser.event.details.control.point.error.existingStartWaypoint"));
        } else if (finishWaypoint) {
          isWaypointValid = false;
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("organiser.event.details.control.point.error.existingFinishWaypoint"));
        }
      }
    }
    return isWaypointValid;
  }

  onCancel() {
    this.currentWaypoint = new Waypoint();
    this.inputForm.reset();
  }

  hasFormAnyValue(): boolean {
    return !!this.currentWaypoint.id || this.inputForm.dirty;
  }

  onDeleteWaypoint(waypoint: Waypoint) {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });

    modalRef.componentInstance.title = this.translocoService.translate("organiser.event.details.control.point.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("organiser.event.details.control.point.delete.dialog.text");

    modalRef.result.then((_): void => {
      this.deleteWaypoint(waypoint);
    }, (_) => {
      /// ignore
    });
  }

  deleteWaypoint(waypoint: Waypoint): void {
    waypoint.raceId = this.race?.id;
    waypoint.eventId = this.event?.id;
    this.loaderService.startLoading();
    this.waypointService.deleteWaypoint(waypoint,
      () => {
        this.currentWaypoint = new Waypoint();
        this.isPointSelectionEnabled = false;
        this.inputForm.reset();
        this.waypointService.findWaypoints(this.race?.id).subscribe((list: Waypoint[]) => {
          this.loaderService.dismissLoading();
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("organiser.event.details.control.point.delete.success"));
          this.waypointList = list;
          this.waypointListChange.emit(list);
        });
      }, () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("organiser.event.details.control.point.delete.error"));
      });
  }

  onEditWaypoint(waypoint: Waypoint): void {
    this.currentWaypoint = waypoint;
    this.inputForm.patchValue(waypoint);

    this.isPointSelectionEnabled = true;
    this.inputForm.controls.pointSelection.setValue(true);

    let requiredFields: FormControl[] = [];
    let nonRequiredFields: FormControl[] = [];
    if (waypoint.waypointTypeId === WaypointType.START_WAYPOINT || waypoint.waypointTypeId === WaypointType.FINISH_WAYPOINT || waypoint.waypointTypeId === WaypointType.START_FINISH_WAYPOINT) {
      requiredFields = [this.inputForm.controls.city, this.inputForm.controls.address,
        this.inputForm.controls.additionalMark, this.inputForm.controls.country];
    } else {
      nonRequiredFields = [this.inputForm.controls.city, this.inputForm.controls.address,
        this.inputForm.controls.additionalMark, this.inputForm.controls.country];
    }
    if (waypoint.waypointTypeId === WaypointType.CONTROL_WAYPOINT) {
      requiredFields = [...requiredFields, this.inputForm.controls.ordinalNumber];
    } else {
      nonRequiredFields = [...nonRequiredFields, this.inputForm.controls.ordinalNumber];
    }
    requiredFields.forEach((control: FormControl) => {
      control.addValidators([required]);
      control.updateValueAndValidity();
    });
    nonRequiredFields.forEach((control: FormControl) => {
      control.removeValidators([required]);
      control.updateValueAndValidity();
    });
  }

  onCopyWaypoint(waypoint: Waypoint) {
    let copyWaypoint = {...waypoint};
    copyWaypoint.id = undefined;
    copyWaypoint.pointNameHr = undefined;
    copyWaypoint.pointNameEn = undefined;
    copyWaypoint.pointNameIt = undefined;
    copyWaypoint.pointNameHr = undefined;
    copyWaypoint.latitude = undefined;
    copyWaypoint.longitude = undefined;
    if (copyWaypoint.waypointTypeId === WaypointType.CONTROL_WAYPOINT) {
      copyWaypoint.ordinalNumber = undefined;
    }
    this.onEditWaypoint(copyWaypoint);
  }

  getWaypointFields(): Waypoint {
    this.checkValidators(this.inputForm);
    let waypoint: Waypoint = {...this.currentWaypoint}
    waypoint.waypointTypeId = this.inputForm.value.waypointTypeId;
    waypoint.pointNameHr = this.inputForm.value.pointNameHr;
    waypoint.pointNameEn = this.inputForm.value.pointNameEn;
    waypoint.pointNameIt = this.inputForm.value.pointNameIt;
    waypoint.pointNameSlo = this.inputForm.value.pointNameSlo;
    waypoint.latitude = this.inputForm.value.latitude;
    waypoint.longitude = this.inputForm.value.longitude;
    waypoint.elevation = parseInt(this.inputForm.value.elevation);
    waypoint.distanceFromStart = parseInt(this.inputForm.value.distanceFromStart);
    waypoint.city = this.inputForm.value.city;
    waypoint.address = this.inputForm.value.address;
    waypoint.additionalMark = this.inputForm.value.additionalMark;
    waypoint.country = this.inputForm.value.country;
    waypoint.descriptionHr = this.inputForm.value.descriptionHr;
    waypoint.descriptionEn = this.inputForm.value.descriptionEn;
    waypoint.descriptionIt = this.inputForm.value.descriptionIt;
    waypoint.descriptionSlo = this.inputForm.value.descriptionSlo;
    if (this.inputForm.value.ordinalNumber) {
      waypoint.ordinalNumber = parseInt(this.inputForm.value.ordinalNumber);
    }
    waypoint.markerTypes = this.inputForm.value.markerTypes;
    waypoint.limit = this.inputForm.value.limit;
    return waypoint;
  }

  processFile(fileInput: any): void {
    if (!this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    } else if (fileInput.files?.length > 0) {
      const file: File = fileInput.files[0];
      if (file.size > environment.maxFileSize) {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.user.profile.max.size.error"));
      } else {
        const reader: FileReader = new FileReader();
        reader.addEventListener('load', (event: any) => {
          this.loaderService.startLoading();
          this.raceRouteService.updateRaceRoute(new RaceRoute(this.race?.id, undefined, event.target.result), () => {
            this.raceRouteService.fetchRaceRoute(this.race!.id!).subscribe((raceRoute: RaceRoute | undefined) => {
              this.loaderService.dismissLoading();
              this.toastService.showToast(TOAST_STATE.success,
                this.translocoService.translate("organiser.event.details.control.point.race.route.save.success"));
              raceRoute!.content = event.target.result;
              this.raceRoute = raceRoute;
              this.raceRouteChange.emit(raceRoute);
              this.parseGpx();
            });
          }, () => {
            this.loaderService.dismissLoading();
            this.toastService.showToast(TOAST_STATE.danger,
              this.translocoService.translate("organiser.event.details.control.point.race.route.save.error"));
          });
        });
        reader.readAsDataURL(file);
      }
    }
  }

  onDeleteGpxFile(): void {
    if (this.fileUploadInputVariable) {
      this.fileUploadInputVariable!.nativeElement.value = "";
    }
    this.loaderService.startLoading();
    this.raceRouteService.deleteRaceRoute(this.race!.id!, () => {
      this.loaderService.dismissLoading();
      this.currentWaypoint = new Waypoint();
      this.isPointSelectionEnabled = false;
      this.inputForm.reset();
      this.raceRoute = undefined;
      this.raceRouteChange.emit(undefined);
      this.gpx = undefined;
      this.toastService.showToast(TOAST_STATE.success,
        this.translocoService.translate("organiser.event.details.control.point.race.route.delete.success"));
    }, () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("organiser.event.details.control.point.race.route.delete.error"));
    });
  }

  getNameColumn() {
    switch (this.translocoService.getActiveLang()) {
      case "hr":
        return "pointNameHr";
      case "en":
        return "pointNameEn";
      case "it":
        return "pointNameIt";
      case "slo":
        return "pointNameSlo";
      default:
        return undefined;
    }
  }

  onCopyStartFromMap() {
    if (this.gpx && this.gpx.getCoords() && this.gpx.getCoords().length && this.gpx.getCoords().length > 0) {
      let location: any = this.gpx!.getCoords()[0];
      this.setSelectedPointData(location);
    }
  }

  onCopyEndFromMap() {
    if (this.gpx && this.gpx.getCoords() && this.gpx.getCoords().length && this.gpx.getCoords().length > 0) {
      let location: any = this.gpx!.getCoords()[this.gpx!.getCoords().length - 1];
      this.setSelectedPointData(location);
    }
  }

  getOptions(): Map<number, string> {
    const optionsMap: Map<number, string> = new Map<number, string>();
    this.waypointMarkerTypes.map((item: Lookups): void => {
      optionsMap.set(item.id!, this.translocoService.translate('app.common.waypoint.marker.types.' + item.id))
    });
    return optionsMap;
  }

  protected readonly Waypoint = Waypoint;
  protected readonly WaypointType = WaypointType;
}
